import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatDate, formatCOP, formatApiError } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { ChevronDown, ChevronRight, ExternalLink, CheckCircle2, Clock, AlertCircle, Circle, FileText } from "lucide-react";
import { usePrompt, PromptDialog } from "../components/usePrompt";
import { toast } from "sonner";

const STATUS_OPTS = ["all", "pending", "pending_confirmation", "confirmed", "failed", "refunded"];
const NIVEL_OPTS = ["all", "nivel1", "nivel2"];

function PayStatusPill({ s }) {
  const map = {
    pending: "bg-amber-50 text-amber-900 border-amber-300",
    pending_confirmation: "bg-amber-50 text-amber-900 border-amber-300",
    confirmed: "bg-emerald-50 text-emerald-900 border-emerald-300",
    failed: "bg-rose-50 text-rose-900 border-rose-300",
    refunded: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return <span className={`inline-flex border px-2 py-[3px] text-[10px] uppercase tracking-wider ${map[s] || "bg-gray-100"}`}>{s}</span>;
}

export default function AdminPaymentsPage() {
  // P2: leer params iniciales de URL para deep-link desde email/badge
  const initialParams = new URLSearchParams(window.location.search);
  const [filters, setFilters] = useState({
    status: initialParams.get("status") || "all",
    nivel: "all",
    from: "",
    to: "",
    q: "",
    has_comprobante: initialParams.get("has_comprobante") === "true",
  });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ total: 0, items: [], pages: 0 });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [pendingReviewCount, setPendingReviewCount] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { payment: p }
  const [expandedId, setExpandedId] = useState(null);  // P2: fila expandida

  // Iter24.e: prompt modal reutilizable (reemplaza window.prompt)
  const { prompt, dialogProps: promptDialogProps } = usePrompt();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page: String(page), limit: "50" });
      if (filters.status !== "all") p.set("status", filters.status);
      if (filters.nivel !== "all") p.set("nivel", filters.nivel);
      if (filters.from) p.set("from_", filters.from);
      if (filters.to) p.set("to", filters.to);
      if (filters.q.trim()) p.set("q", filters.q.trim());
      if (filters.has_comprobante) p.set("has_comprobante", "true");
      const { data: d } = await api.get(`/admin/payments?${p}`);
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando pagos");
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  // P2: cargar count para badge pending-review
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get("/admin/payments/pending-review-count");
        if (alive) setPendingReviewCount(d?.count ?? 0);
      } catch {
        // silencioso
      }
    })();
    return () => { alive = false; };
  }, [data]); // recarga después de cada acción/refresh

  const doAction = async (p, action) => {
    // Si es confirm, abrir el modal en vez de confirmar directamente
    if (action === "confirm") {
      setConfirmModal({ payment: p });
      return;
    }
    // P2 — Para fail/refund pedimos motivo al admin (mínimo 10 chars)
    let reason = "";
    const ref = p.reference || p.payment_id?.slice(0, 8);
    if (action === "fail") {
      reason = await prompt({
        title: "Marcar pago como fallido",
        label: `Motivo para marcar fallido el pago ${ref} (≥10 caracteres):`,
        placeholder: "Ej: usuario no completó el pago en MercadoPago, transferencia rechazada por banco…",
        multiline: true,
        validator: (v) => (v || "").trim().length >= 10 || "El motivo debe tener al menos 10 caracteres",
        testIdPrefix: "payment-fail-reason",
      });
      if (reason === null) return;
    }
    if (action === "refund") {
      reason = await prompt({
        title: "Reembolsar pago",
        label: `Motivo para reembolsar el pago ${ref} (≥10 caracteres):`,
        placeholder: "Ej: pago duplicado, cliente solicitó reembolso por error en el monto…",
        multiline: true,
        validator: (v) => (v || "").trim().length >= 10 || "El motivo debe tener al menos 10 caracteres",
        testIdPrefix: "payment-refund-reason",
      });
      if (reason === null) return;
    }
    setActing(p.payment_id);
    try {
      const URL_BY_ACTION = {
        fail: `/admin/payments/${p.payment_id}/fail`,
        refund: `/admin/payments/${p.payment_id}/refund`,
      };
      const BODY_BY_ACTION = {
        fail: { fail_reason: reason.trim() },
        refund: { refund_reason: reason.trim() },
      };
      await api.post(URL_BY_ACTION[action], BODY_BY_ACTION[action]);
      toast.success("Acción registrada");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error");
    } finally { setActing(null); }
  };

  // Resumen
  const summary = data.items.reduce((acc, p) => {
    if (p.status === "pending" || p.status === "pending_confirmation") acc.pending++;
    if (p.status === "confirmed") acc.confirmed++;
    if (p.status === "refunded") acc.refunded++;
    return acc;
  }, { pending: 0, confirmed: 0, refunded: 0 });

  return (
    <div data-testid="admin-payments-page">
      <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-1">Conciliación</p>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h1 className="font-serif text-3xl text-gray-900">Pagos</h1>
        {pendingReviewCount > 0 && (
          <button
            type="button"
            onClick={() => setFilters((f) => ({
              ...f, status: "pending_confirmation", has_comprobante: true,
            }))}
            className="inline-flex items-center gap-2 border-2 border-amber-500 bg-amber-50 hover:bg-amber-100 text-amber-900 px-4 py-2 rounded-sm text-sm font-medium transition-colors"
            data-testid="pending-review-badge"
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold">
              {pendingReviewCount}
            </span>
            Pagos por revisar
          </button>
        )}
      </div>
      <p className="text-xs text-gray-500 mb-5">{data.total} pagos totales · página actual: pending {summary.pending} · confirmed {summary.confirmed} · refunded {summary.refunded}</p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3 items-end">
        <FilterSelect label="Status" v={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} opts={STATUS_OPTS} testid="pay-filter-status" />
        <FilterSelect label="Nivel" v={filters.nivel} onChange={(v) => setFilters((f) => ({ ...f, nivel: v }))} opts={NIVEL_OPTS} testid="pay-filter-nivel" />
        <div><label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Desde</label><Input type="date" value={filters.from} onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))} className="rounded-sm h-9 text-xs" data-testid="pay-from" /></div>
        <div><label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Hasta</label><Input type="date" value={filters.to} onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))} className="rounded-sm h-9 text-xs" data-testid="pay-to" /></div>
        <div><label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Buscar referencia</label><Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="ref…" className="rounded-sm h-9 text-xs" data-testid="pay-q" /></div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={filters.has_comprobante}
            onChange={(e) => setFilters((f) => ({ ...f, has_comprobante: e.target.checked }))}
            data-testid="filter-has-comprobante"
          />
          <span>Solo con comprobante subido</span>
        </label>
        {(filters.has_comprobante || filters.status !== "all" || filters.from || filters.to || filters.q) && (
          <button
            type="button"
            onClick={() => setFilters({ status: "all", nivel: "all", from: "", to: "", q: "", has_comprobante: false })}
            className="text-[11px] uppercase tracking-wider text-gray-500 hover:text-gray-900 underline-offset-2 hover:underline"
            data-testid="clear-filters"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
            <th className="px-2 py-3 w-8"></th>
            <th className="px-3 py-3 text-left">Referencia</th>
            <th className="px-3 py-3 text-left">Caso</th>
            <th className="px-3 py-3 text-left">Owner</th>
            <th className="px-3 py-3 text-left">Nivel</th>
            <th className="px-3 py-3 text-right">Monto</th>
            <th className="px-3 py-3 text-left">Provider</th>
            <th className="px-3 py-3 text-left">Status</th>
            <th className="px-3 py-3 text-left">🎁 Obsequio</th>
            <th className="px-3 py-3 text-right">Fecha</th>
            <th className="px-3 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-500">Cargando…</td></tr>}
            {!loading && data.items.length === 0 && <tr><td colSpan={11} className="px-4 py-10 text-center text-gray-500" data-testid="payments-empty">Sin resultados.</td></tr>}
            {!loading && data.items.map((p) => {
              const isPending = p.status === "pending" || p.status === "pending_confirmation";
              const isExpanded = expandedId === p.payment_id;
              return (
                <React.Fragment key={p.payment_id}>
                  <tr
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${isExpanded ? "bg-gray-50" : ""}`}
                    data-testid={`pay-row-${p.payment_id}`}
                    onClick={(e) => {
                      // No expandir si el click vino de un botón/enlace en la fila
                      const tag = (e.target?.tagName || "").toUpperCase();
                      if (["BUTTON", "A", "INPUT"].includes(tag)) return;
                      if (e.target.closest("button") || e.target.closest("a")) return;
                      setExpandedId(isExpanded ? null : p.payment_id);
                    }}
                  >
                    <td className="px-2 py-3 text-gray-400">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpandedId(isExpanded ? null : p.payment_id); }}
                        className="p-1 hover:text-gray-900"
                        aria-label={isExpanded ? "Colapsar" : "Expandir"}
                        data-testid={`pay-expand-${p.payment_id}`}
                      >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-3 font-mono text-[11px]">{p.reference || p.referencia || "—"}</td>
                    <td className="px-3 py-3 max-w-[200px] truncate"><Link to={`/admin/cases/${p.case_id}`} className="text-gray-900 hover:underline">{p.case_title || p.case_id?.slice(0, 8)}</Link></td>
                    <td className="px-3 py-3 text-xs text-gray-700 truncate max-w-[160px]">{p.owner_email || p.user_email || "—"}</td>
                    <td className="px-3 py-3 text-xs">{p.nivel}</td>
                    <td className="px-3 py-3 text-right">{formatCOP(p.amount_cop || p.monto_cop)}</td>
                    <td className="px-3 py-3 text-xs">{p.provider || "—"}</td>
                    <td className="px-3 py-3"><PayStatusPill s={p.status} /></td>
                    <td className="px-3 py-3">
                      {p.is_complimentary && (
                        <span className="inline-flex border border-amber-400 bg-amber-100 text-amber-900 px-2 py-[3px] text-[10px] uppercase tracking-wider" data-testid={`pay-complimentary-${p.payment_id}`}>OBSEQUIO</span>
                      )}
                      {p.comprobante_url && (
                        <ComprobanteViewLink
                          caseId={p.case_id}
                          nivel={p.nivel}
                          contentType={p.comprobante_content_type}
                          filename={p.comprobante_filename}
                        />
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500 text-right whitespace-nowrap">{formatDate(p.created_at)}</td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      {isPending && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" className="rounded-sm h-7 text-[11px]" disabled={acting === p.payment_id} onClick={() => doAction(p, "confirm")} data-testid={`pay-confirm-${p.payment_id}`}>Confirmar</Button>
                          <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-rose-300 text-rose-700" disabled={acting === p.payment_id} onClick={() => doAction(p, "fail")} data-testid={`pay-fail-${p.payment_id}`}>Fallido</Button>
                        </div>
                      )}
                      {p.status === "confirmed" && (
                        <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] border-gray-300" disabled={acting === p.payment_id} onClick={() => doAction(p, "refund")} data-testid={`pay-refund-${p.payment_id}`}>Reembolsar</Button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-b border-gray-200" data-testid={`pay-detail-${p.payment_id}`}>
                      <td colSpan={11} className="bg-gray-50/60 px-6 py-5">
                        <PaymentDetailExpanded paymentId={p.payment_id} caseId={p.case_id} nivel={p.nivel} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.pages > 1 && (
        <div className="flex justify-between items-center mt-3 text-xs text-gray-600">
          <span>Página {page} de {data.pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="pay-prev">Anterior</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-8" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="pay-next">Siguiente</Button>
          </div>
        </div>
      )}

      <ConfirmPaymentModal modal={confirmModal} onClose={() => setConfirmModal(null)} onConfirmed={load} />

      {/* Iter24.e — Prompt dialog reutilizable (reemplaza window.prompt) */}
      <PromptDialog {...promptDialogProps} />
    </div>
  );
}

function FilterSelect({ label, v, onChange, opts, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">{label}</label>
      <Select value={v} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm h-9 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o === "all" ? "Todos" : o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

/**
 * ComprobanteViewLink
 * ───────────────────
 * Botón que descarga el comprobante via fetch (cookies httpOnly) y lo abre como blob URL.
 */
function ComprobanteViewLink({ caseId, nivel, contentType, filename }) {
  const [busy, setBusy] = useState(false);
  const onView = async () => {
    setBusy(true);
    try {
      const resp = await api.get(
        `/cases/${caseId}/payments/${nivel}/comprobante`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: contentType || resp.data.type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Liberar tras un tiempo para no romper la pestaña recién abierta
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error abriendo comprobante");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={onView}
      disabled={busy}
      className="block mt-1 text-[10px] uppercase tracking-wider text-primary hover:underline disabled:opacity-50"
      data-testid="comprobante-view-link"
      title={filename || "Ver comprobante"}
    >
      {busy ? "Abriendo…" : "📎 Ver comprobante"}
    </button>
  );
}

/**
 * P2 — Detalle expandible del pago (timeline, thumbnail, USD, SECOP).
 * Fetch lazy on mount; cache simple en estado local (recargar al cerrar/abrir).
 */
function PaymentDetailExpanded({ paymentId, caseId, nivel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgUrl, setImgUrl] = useState(null);

  useEffect(() => {
    let alive = true;
    let blobUrl = null;
    (async () => {
      try {
        setLoading(true);
        const { data: d } = await api.get(`/admin/payments/${paymentId}/detail`);
        if (!alive) return;
        setData(d);
        // Lazy-load thumbnail si es imagen
        const ctype = d?.payment?.comprobante_content_type || "";
        const hasComp = Boolean(d?.payment?.comprobante_url);
        if (hasComp && ctype.startsWith("image/")) {
          try {
            const resp = await api.get(
              `/cases/${caseId}/payments/${nivel}/comprobante`,
              { responseType: "blob" },
            );
            if (!alive) return;
            blobUrl = URL.createObjectURL(new Blob([resp.data], { type: ctype }));
            setImgUrl(blobUrl);
          } catch {
            // sin thumbnail, silencioso
          }
        }
      } catch (e) {
        if (alive) setError(formatApiError(e?.response?.data?.detail) || "Error cargando detalle");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [paymentId, caseId, nivel]);

  if (loading) {
    return <div className="text-xs text-gray-500" data-testid="pay-detail-loading">Cargando detalle…</div>;
  }
  if (error) {
    return <div className="text-xs text-rose-700">{error}</div>;
  }
  if (!data) return null;

  const { payment, case: caseInfo, timeline, secop_comparables: comparables,
    secop_search_url: secopSearchUrl, usd_cop_rate: rate, amount_usd_estimated: amountUsd } = data;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Columna 1 — Comprobante & Caso */}
      <div className="lg:col-span-4 space-y-3">
        <div>
          <h4 className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Comprobante</h4>
          {payment.comprobante_url ? (
            <div className="space-y-2">
              {imgUrl ? (
                <a href={imgUrl} target="_blank" rel="noopener noreferrer">
                  <img src={imgUrl} alt="Comprobante" className="w-full max-w-[280px] border border-gray-200 rounded-sm hover:opacity-90 transition-opacity" data-testid="pay-detail-thumbnail" />
                </a>
              ) : (
                <div className="inline-flex items-center gap-2 border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700">
                  <FileText size={14} />
                  {payment.comprobante_filename || "Archivo"}
                </div>
              )}
              <p className="text-[11px] text-gray-500">
                {payment.comprobante_filename || "comprobante"}
                {payment.comprobante_content_type && <> · {payment.comprobante_content_type}</>}
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Sin comprobante subido aún</p>
          )}
        </div>

        <div className="pt-2 border-t border-gray-200">
          <h4 className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Caso</h4>
          <p className="text-sm text-gray-900 mb-1 line-clamp-2">{caseInfo?.title || "—"}</p>
          {caseInfo?.objeto_contractual && (
            <p className="text-xs text-gray-600 mb-1 line-clamp-3">{caseInfo.objeto_contractual}</p>
          )}
          {caseInfo?.valor_estimado_cop && (
            <p className="text-xs text-gray-700">
              <span className="text-gray-500">Valor estimado:</span> {formatCOP(caseInfo.valor_estimado_cop)}
            </p>
          )}
          <Link
            to={`/admin/cases/${caseInfo?.id}`}
            className="inline-flex items-center gap-1 mt-2 text-[11px] text-primary hover:underline"
            data-testid="pay-detail-case-link"
          >
            Abrir caso completo <ExternalLink size={11} />
          </Link>
        </div>
      </div>

      {/* Columna 2 — Monto + Timeline */}
      <div className="lg:col-span-4 space-y-3">
        <div>
          <h4 className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Monto</h4>
          <p className="font-serif text-2xl text-gray-900">{formatCOP(payment.amount_cop)}</p>
          {amountUsd != null && (
            <p className="text-xs text-gray-600 mt-1">
              ≈ USD {amountUsd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              <span className="text-gray-400 ml-1">(tasa {Math.round(rate)} COP/USD)</span>
            </p>
          )}
        </div>

        <div className="pt-2 border-t border-gray-200">
          <h4 className="text-[10px] uppercase tracking-widest text-gray-500 mb-3">Timeline</h4>
          <ol className="space-y-2" data-testid="pay-detail-timeline">
            {timeline.map((t, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <TimelineIcon event={t.event} current={t.current} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${t.current ? "font-medium text-amber-900" : "text-gray-800"}`}>
                    {t.label}
                  </p>
                  {t.at ? (
                    <p className="text-[10px] text-gray-500">{formatDate(t.at)}</p>
                  ) : (
                    t.current && <p className="text-[10px] text-amber-700">Estado actual</p>
                  )}
                  {t.reason && (
                    <p className="text-[10px] text-gray-600 italic">"{t.reason}"</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Columna 3 — SECOP */}
      <div className="lg:col-span-4">
        <h4 className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">SECOP — Referencias públicas</h4>
        {comparables.length > 0 ? (
          <ul className="space-y-2" data-testid="pay-detail-secop-list">
            {comparables.map((c, idx) => (
              <li key={idx} className="border border-gray-200 bg-white p-2 text-xs">
                <p className="font-medium text-gray-900 truncate" title={c.entidad}>{c.entidad}</p>
                {c.objeto && <p className="text-gray-600 mt-0.5 line-clamp-2">{c.objeto}</p>}
                {c.valor_cop && <p className="text-gray-700 mt-0.5">{formatCOP(c.valor_cop)}</p>}
                {c.fuente_url && (
                  <a
                    href={c.fuente_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline mt-1 text-[11px]"
                  >
                    Ver en SECOP <ExternalLink size={10} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        ) : secopSearchUrl ? (
          <div className="border border-dashed border-gray-300 bg-white p-3 text-xs">
            <p className="text-gray-600 mb-2">No hay comparables guardados para este caso.</p>
            <a
              href={secopSearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
              data-testid="pay-detail-secop-search"
            >
              Buscar en SECOP por objeto <ExternalLink size={11} />
            </a>
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">Sin objeto contractual definido para buscar en SECOP</p>
        )}
      </div>
    </div>
  );
}

function TimelineIcon({ event, current }) {
  const cls = "shrink-0 mt-[2px]";
  if (current) return <Clock size={14} className={`${cls} text-amber-600`} />;
  if (event === "payment_confirmed") return <CheckCircle2 size={14} className={`${cls} text-emerald-700`} />;
  if (event === "payment_failed") return <AlertCircle size={14} className={`${cls} text-rose-700`} />;
  if (event === "payment_refunded") return <AlertCircle size={14} className={`${cls} text-gray-500`} />;
  return <Circle size={14} className={`${cls} text-gray-400`} />;
}



function ConfirmPaymentModal({ modal, onClose, onConfirmed }) {
  const [form, setForm] = useState({
    method: "bank_transfer",
    referencia: "",
    notes: "",
    is_complimentary: false,
    complimentary_reason: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (modal) {
      setForm({
        method: "bank_transfer",
        referencia: "",
        notes: "",
        is_complimentary: false,
        complimentary_reason: "",
      });
    }
  }, [modal]);

  const open = !!modal;
  const p = modal?.payment;

  const save = async () => {
    if (form.is_complimentary && form.complimentary_reason.trim().length < 10) {
      toast.error("Motivo del obsequio debe tener mínimo 10 caracteres");
      return;
    }
    setSaving(true);
    try {
      const body = {
        method: form.method,
        referencia: form.referencia.trim() || undefined,
        notes: form.notes.trim() || undefined,
        is_complimentary: form.is_complimentary,
        complimentary_reason: form.is_complimentary ? form.complimentary_reason.trim() : undefined,
      };
      await api.post(`/admin/cases/${p.case_id}/payments/${p.nivel}/confirm`, body);
      toast.success(form.is_complimentary ? "Obsequio registrado" : "Pago confirmado");
      onConfirmed();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error confirmando pago");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none border border-gray-200 max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Confirmar pago manual</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Método de pago</label>
            <Select value={form.method} onValueChange={(v) => setForm((f) => ({ ...f, method: v }))}>
              <SelectTrigger className="rounded-sm h-9 text-xs" data-testid="confirm-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Transferencia bancaria</SelectItem>
                <SelectItem value="qr_bre_b">QR Bre-B</SelectItem>
                <SelectItem value="external_link">Link externo (epayco/PayU)</SelectItem>
                <SelectItem value="comprobante_subido">Comprobante subido</SelectItem>
                <SelectItem value="complimentary">Complimentary</SelectItem>
                <SelectItem value="other">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Referencia (opcional)</label>
            <Input
              value={form.referencia}
              onChange={(e) => setForm((f) => ({ ...f, referencia: e.target.value }))}
              placeholder="Ej: REF-12345"
              className="rounded-sm h-9 text-xs"
              data-testid="confirm-referencia"
            />
          </div>

          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">Notas (opcional)</label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              maxLength={2000}
              className="rounded-sm text-xs"
              placeholder="Observaciones adicionales..."
              data-testid="confirm-notes"
            />
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_complimentary}
                onChange={(e) => setForm((f) => ({ ...f, is_complimentary: e.target.checked }))}
                className="rounded"
                data-testid="confirm-is-complimentary"
              />
              <span>🎁 Marcar como obsequio (gratis al usuario)</span>
            </label>
          </div>

          {form.is_complimentary && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-sm">
              <label className="block text-[10px] uppercase tracking-widest text-amber-900 mb-1">
                Motivo del obsequio (obligatorio, min 10 caracteres)
              </label>
              <Textarea
                value={form.complimentary_reason}
                onChange={(e) => setForm((f) => ({ ...f, complimentary_reason: e.target.value }))}
                rows={2}
                maxLength={500}
                className="rounded-sm text-xs bg-white"
                placeholder='Ej: "Demo a entidad pública X", "Compensación por bug Y"'
                data-testid="confirm-complimentary-reason"
              />
              <p className="text-[10px] text-amber-800 mt-1">
                {form.complimentary_reason.trim().length}/500 caracteres · mínimo 10
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="confirm-cancel">Cancelar</Button>
          <Button className="rounded-sm" disabled={saving} onClick={save} data-testid="confirm-submit">
            {saving ? "Confirmando…" : "Confirmar pago"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

